import React, { useState } from "react";
import { Col, Form, FormControl, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import StbButton from "../../../atoms/button/StbButton";
import { IModalAddContactProps } from "./ModalAddContact.types";
import { useQueryClient } from "@tanstack/react-query";
import { getGetApiContactsCustomerCustomerNumberQueryKey, usePostApiContacts } from "../../../../api/generated/endpoints/contact/contact";
import type { ContactCreateDto as CreateContactDto, ContactSummaryDto } from "../../../../api/generated/model";
import { modalAddContactSchema } from "../../../../validations/ModalAddContactValidation";

const ModalAddContact: React.FC<IModalAddContactProps> = (props) => {
  const [name, setName] = useState<string>("");
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([""]);
  const [phoneErrors, setPhoneErrors] = useState<(string | null)[]>(phoneNumbers.map(() => null));
  const [emailAddresses, setEmailAddresses] = useState<string[]>([""]);
  const [emailErrors, setEmailErrors] = useState<(string | null)[]>(emailAddresses.map(() => null));
  const [nameError, setNameError] = useState<string>("");
  const queryClient = useQueryClient();

  const createContactMutation = usePostApiContacts({
    mutation: {
      onSuccess: (data) => {
        if (data !== null && data.id !== undefined) {
          if (props.customerNumber) {
            const queryKey = getGetApiContactsCustomerCustomerNumberQueryKey(props.customerNumber);
            queryClient.setQueryData(queryKey, (previous: ContactSummaryDto[] | undefined) => {
              if (!previous) {
                return [data];
              }
              return [...previous, data];
            });
            queryClient.invalidateQueries({ queryKey });
          }
          props.setCustomerContactList((prevContacts) => [...prevContacts, data]);
          props.setIsContactListUpdated(true);
          props.setShowConfirmationMessage(true);
          props.setSelectedContactsIds((prevIds) => [...prevIds, data.id as number]);
        }
        props.setIsContactListUpdated(true);
        props.setIsModalAddContactVisible(false);
      },
      onError: (error: any) => {
        props.setAddContactErrorMessage(error?.message || "Ett fel uppstod");
        props.setIsContactListUpdated(true);
      },
    },
  });

  const handleAddPhoneNumber = () => {
    if (phoneNumbers.length < 3) {
      setPhoneNumbers([...phoneNumbers, ""]);
      setPhoneErrors([...phoneErrors, null]);
    }

  };

  const handlePhoneNumberChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^\d*$/.test(value) || value === "") {
        const newPhoneNumbers = [...phoneNumbers];
        newPhoneNumbers[index] = value;
        setPhoneNumbers(newPhoneNumbers);
        validatePhoneNumber(index, value);
      }
    };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\sÅÄÖåäö]+$/.test(value) || value === "") {
      setName(value);
      if (value.length < 4) {
        setNameError("Fullständiga namnet måste vara längre!");
      } else {
        setNameError("");
      }
    }
  };

  const validatePhoneNumber = (index: number, phoneNumber: string) => {
    const newPhoneErrors = [...phoneErrors];
    if (phoneNumber.length < 6) {
      newPhoneErrors[index] = "Ett telefonnummer är för kort";
    } else {
      newPhoneErrors[index] = null;
    }
    setPhoneErrors(newPhoneErrors);
  };

  const handleAddEmailAddress = () => {
    if (emailAddresses.length < 3) {
      setEmailAddresses([...emailAddresses, ""]);
      setEmailErrors([...emailErrors, null]);
    }
  };

  const handleEmailAddressChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmailAddresses = [...emailAddresses];
      newEmailAddresses[index] = e.target.value;
      setEmailAddresses(newEmailAddresses);
      validateEmailAddress(index, e.target.value);
    };

  const validateEmailAddress = (index: number, emailAddress: string) => {
    const newEmailErrors = [...emailErrors];
    if (emailAddress.length < 4 || !emailAddress.includes("@") || !emailAddress.includes(".")) {
      newEmailErrors[index] = "Mailadressen är inte giltig";
    } else {
      newEmailErrors[index] = null;
    }
    setEmailErrors(newEmailErrors);
  };

  const handleSaveContact = async () => {
    if (name.length < 4) {
      setNameError("Fullständiga namnet måste vara längre!");
      return;
    } else {
      setNameError("");
    }

    const newContact: CreateContactDto = {
      name,
      phoneNumbers,
      emailAddresses,
      customerNumber: props.customerNumber!,
    };

    // Validate contact schema
    const isContactValid: boolean = await modalAddContactSchema.isValid(
      newContact
    );

    // If contact is valid, proceed to save
    if (isContactValid) {
      createContactMutation.mutate({ data: newContact });
    }
  };

  const handleCloseModal = () => {
    props.setIsModalAddContactVisible(false);
  };

  return (
    <Modal show={props.isModalAddContactVisible} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>Lägg till ny kontakt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSaveContact}>
          <Row className="mb-4">
            <Col>
              <h5>Namn</h5>
              <FormControl
                type="text"
                placeholder="Fullständigt namn"
                value={name}
                required
                onChange={handleNameChange}
                isInvalid={nameError !== "" || (name.length > 0 && name.length < 4)}
                isValid={name.length >= 4 && nameError === ""}
              />
              <Form.Control.Feedback type="invalid">
                {nameError}
              </Form.Control.Feedback>
            </Col>
          </Row>
          {phoneNumbers.map((phoneNumber, index) => (
            <Row className="mb-4" key={index}>
              <Col>
                <h5>Telefonnummer {index > 0 ? index + 1 : ""}</h5>
                <FormControl
                  type="text"
                  placeholder="Telefonnummer"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange(index)}
                  isInvalid={phoneErrors[index] !== null}
                  isValid={
                    phoneErrors[index] === null && phoneNumber.length >= 5
                      ? true
                      : phoneErrors[index] === null
                        ? undefined
                        : !phoneErrors[index]
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {phoneErrors[index]}
                </Form.Control.Feedback>
              </Col>
            </Row>
          ))}
          {emailAddresses.map((emailAddress, index) => (
            <Row className="mb-4" key={index}>
              <Col>
                <h5>Mailadress {index > 0 ? index + 1 : ""}</h5>
                <FormControl
                  type="text"
                  placeholder="Mailadress"
                  value={emailAddress}
                  onChange={handleEmailAddressChange(index)}
                  isInvalid={emailErrors[index] !== null}
                  isValid={
                    emailAddresses[index].length > 0 && emailErrors[index] === null
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {emailErrors[index]}
                </Form.Control.Feedback>
              </Col>
            </Row>
          ))}
          <Row>
            <Col className="d-flex justify-content-center my-2">
              <StbButton variant="outline" onClick={handleAddPhoneNumber}>
                Lägg till fler telefonnummer
              </StbButton>
              <StbButton variant="outline" className="ms-3" onClick={handleAddEmailAddress}>
                Lägg till fler mailadresser
              </StbButton>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <StbButton variant="secondary" onClick={handleCloseModal}>
          Stäng
        </StbButton>
        <StbButton
          variant="primary"
          type="submit"
          onClick={handleSaveContact}
          disabled={!name}
        >
          Spara kontakt
        </StbButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddContact;